import React, { useState } from 'react';
import toast from 'react-hot-toast';
import Header from "../../components/Header/Header";
import SideNavbar from "../../components/Navbar/SideNavbar";
import { updateCustomerPaymentMode, PaymentModeResponse } from '../../../core/services/paymentModeService';

const LABEL_CLASS = "block text-xs mb-1.5 text-[#5E5D5D] font-medium";
const INPUT_CLASS = "w-full h-9 rounded border text-[#555] border-[#EAEAEA] px-2.5 outline-none text-sm font-light focus:border-blue-400 disabled:bg-gray-100";
const HINT_CLASS = "text-[11px] text-[#858585] font-light mt-1.5";

const PAYMENT_MODES = [
  { value: 'PREPAID', label: 'Prepaid Only', description: 'Customer can only make prepaid payments' },
  { value: 'COD', label: 'Allow COD', description: 'Customer can use Cash on Delivery' },
];

const PaymentModeManagement: React.FC = () => {
  const [customerId, setCustomerId] = useState<string>('');
  const [paymentMode, setPaymentMode] = useState<'COD' | 'PREPAID'>('PREPAID');
  const [reason, setReason] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!customerId) {
      toast.error('Please enter Customer ID');
      return;
    }
    if (!reason.trim()) {
      toast.error('Please enter reason for change');
      return;
    }
    const isConfirmed = window.confirm(
      `Are you sure you want to update payment mode for Customer ID ${customerId} to ${paymentMode}?`
    );
    if (!isConfirmed) {
      return;
    }

    try {
      setLoading(true);
      const isCod = paymentMode === 'COD';

      const response: PaymentModeResponse = await updateCustomerPaymentMode(
        Number(customerId),
        isCod,
        reason
      );

      if (response.status) {
        toast.success(`Payment mode updated successfully to ${paymentMode} for Customer ID ${customerId}`);
        // Reset form
        setCustomerId('');
        setReason('');
        setPaymentMode('PREPAID');
      } else {
        toast.error(response.statusDescription || 'Failed to update payment mode');
      }
    } catch (error: any) {
      console.error('Error updating payment mode:', error);
      const errorMessage = error?.response?.data?.statusDescription || error?.message || 'Failed to update payment mode';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex mainWrapper w-full">
      <div className="sidenav-wrapper">
        <SideNavbar />
      </div>
      <div className="right-container">
        <div className="header-container">
          <Header />
        </div>
        <div className="body-container">
          <div className="p-4 flex items-center">
            <p className="text-xl font-bold text-[#43425D]">PAYMENT MODE MANAGEMENT</p>
          </div>

          <div className="px-4 pb-5">
            <form onSubmit={handleSubmit}>
              <div className="w-full bg-white shadow-[0_1px_4px_rgba(0,0,0,.2)] rounded mb-5">
                <div className="bg-[#f6f8f8] p-4 border-b border-b-[#efeff4]">
                  <p className="text-base text-[#43425D]">Update Payment Mode</p>
                  <p className="text-xs text-[#858585] font-light mt-0.5">
                    Set a customer as Prepaid only, or allow Cash on Delivery
                  </p>
                </div>
                <div className="py-4 px-2.5">
                  <div className="flex w-full flex-wrap">
                    <div className="w-full md:w-1/2 px-2 mb-6">
                      <label htmlFor="customerId" className={LABEL_CLASS}>
                        Customer ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        id="customerId"
                        value={customerId}
                        onChange={(e) => setCustomerId(e.target.value)}
                        placeholder="e.g., 11311"
                        className={INPUT_CLASS}
                        required
                        disabled={loading}
                      />
                      <p className={HINT_CLASS}>The unique customer ID whose payment mode changes</p>
                    </div>

                    <div className="w-full md:w-1/2 px-2 mb-6">
                      <label className={LABEL_CLASS}>
                        Payment Mode <span className="text-red-500">*</span>
                      </label>
                      <div className="flex flex-col gap-2 pt-1">
                        {PAYMENT_MODES.map((mode) => (
                          <label key={mode.value} htmlFor={mode.value} className="flex items-start cursor-pointer">
                            <input
                              id={mode.value}
                              name="paymentMode"
                              type="radio"
                              value={mode.value}
                              checked={paymentMode === mode.value}
                              onChange={(e) => setPaymentMode(e.target.value as 'PREPAID' | 'COD')}
                              className="h-4 w-4 mt-0.5 text-blue-600 focus:ring-blue-500 border-gray-300"
                              disabled={loading}
                            />
                            <span className="ml-2.5">
                              <span className="block text-sm text-[#555]">{mode.label}</span>
                              <span className="block text-[11px] text-[#858585] font-light">{mode.description}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="w-full px-2">
                      <label htmlFor="reason" className={LABEL_CLASS}>
                        Reason for Change <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Enter reason for changing payment mode..."
                        rows={3}
                        className="w-full rounded border text-[#555] border-[#EAEAEA] p-2.5 outline-none text-sm font-light focus:border-blue-400 disabled:bg-gray-100 resize-y"
                        required
                        disabled={loading}
                      />
                      <p className={HINT_CLASS}>Recorded against the change — explain why the payment mode is being updated</p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !customerId || !reason.trim()}
                className="bg-blue-500 text-white px-8 rounded h-9 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Updating..." : `Update Payment Mode to ${paymentMode}`}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModeManagement;
